import type { ComponentProps } from 'react';
import { useMemo } from 'react';
/* ⚠️ `formatHours` went with the havi óra column — PHASE-5.md §4. The figure is still on the
   person's own screen and on the Logolás dashboard, both of which format it themselves. */
import type { Metafield, Person, TeamRef } from '@forecastify/shared';
import {
  DataList,
  type DataListColumn,
  type DataListRow,
  type MenuItem,
  type SortState,
} from '@forecastify/ui';
import { useI18n, useT, type Translate } from '../../i18n';
import { personName } from '../person-name';
import { metafieldCells, metafieldColumns } from '../metafield-columns';
import { useAvatars } from '../use-avatar';
import { teamFilter } from '../use-teams';
import { ListHeader, usePageHeader } from '../page-header';
import { listLabels } from '../delivery/clients-screen';
import { initialsOf } from '../identity';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Dolgozók — the people list. `main.jsx` 4779–4832.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Nine columns and five filters, all the mockup's. One `DataList`, no bespoke table.
 *
 * ⚠️ **The mockup's list also carries INVITE rows** — pending invitations rendered as people with an
 * `@` avatar. Invitations are not built: there is no invite table and no send path, and C17 owns the
 * notification and invitation surfaces. A row that said *"meghívó elküldve"* over nothing would be
 * the worst kind of placeholder, so the list shows members and the absence is recorded in
 * `chunks.md`.
 *
 * ⚠️ **Filtering and searching are SERVER-SIDE**, unlike the client list's. A staff list is bounded,
 * so there is no cursor — but the filters were free to be either, and after C12 the default is the
 * server: a filter that lives in the route cannot disagree with a later page.
 */

export interface PeopleScreenProps {
  /**
   * ⚠️ The page controls. Absent means the list is not paged — which is how every embedded copy of
   * this table stays unchanged (`state-rules.md` §6: consistent WITHIN a screen).
   */
  readonly page?: ComponentProps<typeof DataList>['page'];
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly people: readonly Person[];
  /** R-60 · the workspace's teams — the filter's options and the bulk menus. */
  readonly teams: readonly TeamRef[];
  readonly search: string;
  readonly team: string;
  readonly status: string;
  readonly employmentType: string;
  readonly tracks: string;
  /** ux-pass-2 · *Erőforrás terv* — `''`, `'true'` or `'false'`, exactly as `tracks`. */
  readonly plan?: string;
  readonly includeDeleted: boolean;
  /* ⚠️ `canManage` was here until C17 and is gone: the only thing it gated was the sentence that
   * stood in for the invitation list, and the panel that replaced it carries its own permission. A
   * prop no component reads is a contract callers have to satisfy for nothing. */
  readonly canDeactivate: boolean;
  /** `people.onboard` — the mockup's own key, and NOT `people.manage`. */
  readonly canOnboard: boolean;
  readonly serverError: string | null;
  readonly onSearch: (value: string) => void;
  readonly onTeam: (value: string) => void;
  readonly onStatus: (value: string) => void;
  readonly onEmploymentType: (value: string) => void;
  readonly onTracks: (value: string) => void;
  readonly onPlan?: (value: string) => void;
  readonly onIncludeDeleted: (value: boolean) => void;
  /**
   * ⚠️ **Controlled, and the SERVER does the ordering** — `DataList` never reorders the rows it is
   * handed (CLAUDE.md §3). The array's order is the priority; item 45.
   */
  readonly sort: readonly SortState[];
  readonly onSortChange: (next: readonly SortState[]) => void;
  /**
   * ⚠️ **Item 72 · team and employment type, and not the rest of a person.** *"Move these six to
   * Delivery"* is a property of a selection; a name or a phone number is not, and a bulk field
   * that writes one person's fact onto six is a data-loss button wearing a convenience label.
   *
   * ⚠️ Optional together, so the embedded copies and the tests keep working without them.
   */
  readonly onBulkEdit?: (
    ids: readonly string[],
    patch: { addTeamId?: string; removeTeamId?: string; employmentType?: string },
  ) => void;
  /** ⚠️ **A TOMBSTONE, not a delete** — P-2, exactly as the row menu's own item. */
  readonly onBulkDeactivate?: (ids: readonly string[]) => void;
  /** ⚠️ Item 44 · the two things a pending-invitation row can do. Takes the INVITATION's id. */
  readonly onResendInvite?: (invitationId: string) => void;
  readonly onRevokeInvite?: (invitationId: string) => void;
  /** ⚠️ Item 27 · the workspace's `person` fields. Those with `inList` become columns. */
  readonly metafields?: readonly Metafield[];
  readonly onOpen: (person: Person) => void;
  readonly onSetStatus: (person: Person, status: 'active' | 'deactivated') => void;
  readonly onDelete: (person: Person) => void;
  readonly onOnboard: (person: Person) => void;
  /**
   * ux-pass-2 · the row menu's *Erőforrás-tervezés ki/bekapcsolása* — the phase 5 mockup's own
   * item. ⚠️ Absent without `people.manage`, which is what the server checks on the PATCH.
   */
  readonly onSetPlanned?: (person: Person, planned: boolean) => void;
  /**
   * C17's invitation block.
   *
   * ⚠️ **A NODE, not a list of invitations.** This screen is presentational — it composes components
   * and does not fetch — and the invitation panel loads its own data because none of the seven
   * requests the people route already makes has anything to do with it.
   */
  readonly invitations?: React.ReactNode;
  /**
   * C35 · remote accounts paired with people (§5.10).
   *
   * ⚠️ A NODE, for the same reason `invitations` is one: this screen composes, it does not fetch.
   */
  /** Opens the invite dialog. Absent when the caller lacks `people.onboard`. */
  readonly onInvite?: () => void;
  /**
   * ⚠️ **Drawn inside something else — the setup flow's picture of the product** (ux-pass-2).
   * Exactly `TasksScreen`'s prop and for its reasons: an embedded copy claims neither the shell's
   * header nor the `data-screen` marker the route-render gate reads.
   */
  readonly embedded?: boolean;
}

export function statusLabel(t: Translate, status: Person['status']): string {
  /* One `t()` per branch with a literal key — a computed key is invisible to anything scanning. */
  if (status === 'active') return t('people.status.active');
  if (status === 'invited') return t('people.status.invited');
  return t('people.status.deactivated');
}

export function employmentLabel(t: Translate, type: Person['employmentType']): string {
  return type === 'contractor'
    ? t('people.employment.contractor')
    : t('people.employment.internal');
}

export function PeopleScreen(props: PeopleScreenProps) {
  const t = useT();
  /**
   * ⚠⚠️ **`Meghívás` IN THE HEADER, WHERE THE MOCKUP DRAWS IT** (`markup.html` 229–232) — and
   * it was at the very BOTTOM of this screen, under the invitations panel. That is the complaint
   * behind *"the invite is on the bottom"*: the one action this screen exists for was the last thing
   * on the page, below a list that can be a hundred rows long.
   *
   * ⚠️ **The panel keeps its own controls** — resend and revoke act on a row, not on the screen.
   * Only the primary action moves.
   */
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE CREATE ACTION IS IN THE TOOLBAR, NOT THE HEADER — item 25, 15 Sep.│
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * *"They all should look like contracts, one line with the search, not purple."* Contracts have
   * passed their create through `DataList`'s `actions` since C30 and every other list grew a purple
   * header CTA instead, so the same act looked like two different things depending on which list
   * you were standing in.
   *
   * ⚠️ **The header keeps the TITLE and nothing else.** A header action is right for a screen whose
   * primary act is not about a list — a task detail, a project — and wrong here, where the toolbar
   * already holds the search, the filters and the columns that the create belongs beside.
   */
  usePageHeader(() => <ListHeader title={t('title.people')} />, [], props.embedded !== true);

  const { people, teams, canDeactivate, canOnboard, serverError } = props;

  /* ⚠️ The PURE helper rather than `usePersonName()` here: the rows are memoised, and a hook that
     returns a fresh closure each render would be a dependency that changes every render. */
  const { locale } = useI18n();

  /* ⚠️ One request per distinct picture for the whole list — see `use-avatar.ts`. */
  const avatars = useAvatars(people.map((person) => person.avatarUrl));

  const rows: DataListRow[] = useMemo(
    () =>
      people.map((person) => ({
        id: person.id,
        cells: {
          name: {
            avatar: {
              /* ⚠️ **An `@` for an invitation, which is the mockup's own mark for one** — item 44,
                 and it is what keeps a row with no picture and no name from reading as a person
                 whose avatar failed to load. */
              initials: person.kind === 'invitation' ? '@' : initialsOf(personName(person, locale)),
              /* ⚠️⚠️ **AN INVITATION KEEPS ITS `@` AND DOES NOT GET A FACE.** The seed is
                 there — an invitation is an address and nothing else — so a face would draw
                 perfectly, and it would be the face they get on the day they accept. That is
                 exactly the problem: the `@` is how this list says *nobody is here yet*, and a
                 row with a person's face on it does not read as an empty chair. */
              ...(person.kind === 'invitation' || person.avatarSeed === null
                ? {}
                : { seed: person.avatarSeed }),
              title: personName(person, locale),
              src: avatars.get(person.avatarUrl ?? ''),
            },
            /* ⚠️ Item 6 · drawn in the READER's order. The list still SORTS on `display_name`,
               which is the canonical family-first form — sorting on what the screen happens to
               render would reorder the list when somebody changed their language. */
            text: personName(person, locale),
            bold: true,
            /* ⚠️ The mockup glues *" · alvállalkozó"* to the name; a sub-line keeps the name
             * sortable and needs no Hungarian suffix on a value. */
            sub:
              person.kind === 'invitation'
                ? t('people.invitationSub')
                : person.employmentType === 'contractor'
                  ? t('people.employment.contractor')
                  : undefined,
            muted: person.deletedAt !== null || person.kind === 'invitation',
          },
          email: { text: person.email, muted: true },
          roles: {
            /* ⚠️ A list, because effective permissions are the merge of a member's roles (C5). The
             * mockup shows one `plevel` because it has one; joining them is the honest rendering.
             *
             * ⚠️ **An em-dash for an invitation, not a blank.** The role IS fixed at invitation
             * time (`0027`), but this list does not read it — and an empty cell says *"none"*
             * where an em-dash says *"not applicable yet"*, which is the true one. */
            text:
              person.roles.length === 0 ? '–' : person.roles.map((role) => role.name).join(', '),
            muted: person.roles.length === 0,
          },
          status: {
            badge: {
              label:
                person.deletedAt === null
                  ? statusLabel(t, person.status)
                  : t('people.status.deleted'),
              variant:
                person.deletedAt !== null
                  ? ('outline' as const)
                  : person.status === 'active'
                    ? ('default' as const)
                    : ('secondary' as const),
            },
          },
          tracks: {
            badge: {
              label: person.tracksTime ? t('people.tracks.yes') : t('people.tracks.no'),
              variant: person.tracksTime ? ('secondary' as const) : ('outline' as const),
            },
          },
          /* ux-pass-2 · ⚠️ An em-dash for an invitation: nobody has decided whether somebody who
             has not arrived yet will be planned. */
          plan:
            person.kind === 'invitation'
              ? { text: '–', muted: true }
              : {
                  badge: {
                    label: person.resourcePlanned ? t('people.plan.yes') : t('people.plan.no'),
                    variant: person.resourcePlanned ? ('secondary' as const) : ('outline' as const),
                  },
                },
          /* ⚠️ An em-dash rather than `0` for an invitation — nobody is on nothing yet, and a
             column of zeros cannot tell *"none"* from *"not yet"*. */
          projects:
            person.kind === 'invitation'
              ? { text: '–', muted: true }
              : { text: person.projectCount, mono: true },
          /* ⚠️ **Item 27 · the workspace's own fields**, for every definition whose `inList` is on.
             ⚠️ An invitation's map is empty, so every one of them renders as an em-dash — which is
             the honest answer and the same one the figures above give. */
          ...metafieldCells(t, props.metafields ?? [], person.metafields),
        },
      })),
    [people, t, locale, avatars, props.metafields],
  );

  const columns: DataListColumn[] = [
    /* ⚠️ Every `sortKey` is a name in `PERSON_SORT`, the server's catalogue (item 45, D3). ⚠️ And
       `roles` and `tracks` deliberately carry none: a member's roles are an ARRAY, which has no
       ordering anybody would recognise, and a two-value boolean sorts into two blocks. */
    { key: 'name', label: t('people.column.person'), sortKey: 'name' },
    { key: 'email', label: t('people.column.email'), width: 230, sortKey: 'email' },
    { key: 'roles', label: t('people.column.roles'), width: 170 },
    { key: 'status', label: t('people.column.status'), width: 120, sortKey: 'status' },
    { key: 'tracks', label: t('people.column.tracks'), width: 130 },
    /* ux-pass-2 · the mockup's `plan` column, 130px — ⚠️ OFF until somebody turns it on in the
       column picker, because most rows say *Tervezett* and the filter answers the question. */
    { key: 'plan', label: t('people.column.plan'), width: 130, defaultHidden: true },
    /**
     * ┌────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ CSAPAT AND HAVI ÓRA CAME OFF HERE — PHASE-5.md §4.                │
     * └────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **THE COLUMNS, NOT THE CONCEPT, AND THE DIFFERENCE IS THE WHOLE NOTE.** The team
     * is still searched (`u.n + ' ' + u.mail + ' ' + px.team`), still filtered (`peopleTeamF`),
     * still bulk-edited and still on the person's own screen — all four are in the phase 5
     * mockup. What went is a 130px column of a value most rows leave blank, and a figure whose
     * question (*"how many hours this month"*) the Logolás dashboard answers per day.
     *
     * ⚠️ **This is the ONE removal of §4's three that was real work.** The other two — the
     * `pcMode` switcher and the Feladatok Kockázat column — we never built, which a mockup diff
     * cannot tell you and a pass over our own source can.
     */
    {
      key: 'projects',
      label: t('people.column.projects'),
      width: 90,
      align: 'right',
      sortKey: 'project_count',
    },
    /* ⚠️ No `sortKey` — `metafield-columns.ts` argues it. */
    ...metafieldColumns(props.metafields ?? []),
  ];

  const rowMenu = (id: string): MenuItem[] => {
    const person = people.find((candidate) => candidate.id === id);
    if (person === undefined) return [];

    /**
     * ⚠️⚠️ **AN INVITATION IS NOT A PERSON, AND EVERY ITEM BELOW WOULD BE REFUSED FOR ONE.** Item
     * 44 puts pending invitations in this list; `person.id` on such a row is the INVITATION's id,
     * so *Megnyitás* would navigate to `/people/<an invitation>` and deactivate would send a member
     * id the server has never seen. It gets the two actions it actually has.
     */
    if (person.kind === 'invitation') {
      if (!canOnboard) return [];
      return [
        {
          label: t('invite.resend'),
          icon: 'mail',
          onSelect: () => {
            props.onResendInvite?.(person.id);
          },
        },
        {
          label: t('invite.revoke'),
          icon: 'trash',
          destructive: true,
          onSelect: () => {
            props.onRevokeInvite?.(person.id);
          },
        },
      ];
    }

    const items: MenuItem[] = [
      {
        label: t('people.action.open'),
        icon: 'arrow-right',
        onSelect: () => {
          props.onOpen(person);
        },
      },
    ];

    /* ux-pass-2 · the mockup's toggle, worded by the state it would leave. */
    if (person.deletedAt === null && props.onSetPlanned !== undefined) {
      const setPlanned = props.onSetPlanned;
      items.push({
        label: person.resourcePlanned ? t('people.action.planOff') : t('people.action.planOn'),
        icon: 'layout-grid',
        /* U-20 · offered and greyed, with the reason, rather than hidden: a missing item reads as
           a missing permission. */
        ...(person.tracksTime
          ? {}
          : { disabled: true, description: t('people.plan.needsTracking') }),
        onSelect: () => {
          setPlanned(person, !person.resourcePlanned);
        },
      });
    }

    /**
     * ⚠️ **Above the deactivate gate, because onboarding is a DIFFERENT permission.** `people.onboard`
     * is the mockup's key for *"meghívás és onboarding"*; somebody who holds it and not
     * `people.deactivate` still gets this item, and hanging it off the wrong check would have hidden
     * it from exactly the person the permission exists for.
     */
    if (person.deletedAt === null && canOnboard) {
      items.push({ type: 'separator' });
      items.push({
        /* ⚠️ Two labels, not one with a suffix: "indítása" and "újranyitása" are the mockup's, and
         * reopening a finished onboarding is worth saying out loud. */
        label:
          person.onboardedAt === null
            ? t('people.action.onboardStart')
            : t('people.action.onboardReopen'),
        icon: 'sparkles',
        onSelect: () => {
          props.onOnboard(person);
        },
      });
    }

    /* ⚠️ A tombstone offers nothing but the open — P-2 froze it, and a menu of actions that would
     * all be refused is a menu that teaches somebody the product is broken. */
    if (person.deletedAt !== null || !canDeactivate) return items;

    items.push({ type: 'separator' });
    if (person.status === 'active') {
      items.push({
        label: t('people.action.deactivate'),
        icon: 'flag',
        onSelect: () => {
          props.onSetStatus(person, 'deactivated');
        },
      });
    } else {
      items.push({
        label: t('people.action.activate'),
        icon: 'check',
        onSelect: () => {
          props.onSetStatus(person, 'active');
        },
      });
    }

    items.push({
      label: t('people.action.delete'),
      icon: 'trash',
      /* ⚠️ A neutral outline everywhere else; here `destructive` marks a menu ITEM, which the kit
       * allows — what it reserves is the filled danger BUTTON. */
      destructive: true,
      /* ⚠️ **Disabled while active, rather than absent**, because T-7's sequence is the thing worth
       * teaching: deactivate first. An absent item would leave somebody hunting for it. */
      disabled: person.status !== 'deactivated',
      /* 30 Oct 2026 · and it says why — disabled with no reason was the one item that did not. */
      ...(person.status === 'deactivated'
        ? {}
        : { description: t('people.action.deleteNeedsDeactivate') }),
      onSelect: () => {
        props.onDelete(person);
      },
    });

    return items;
  };

  return (
    <div
      {...(props.embedded === true ? {} : { 'data-screen': 'people' })}
      className="flex min-h-0 flex-1 flex-col"
    >
      {serverError !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive m-3 rounded-md border px-3 py-2 text-[12.5px]"
        >
          {serverError}
        </p>
      )}

      {/* ⚠️ Edge to edge: the mockup drops its `DataList` straight into the content area
      (`markup.html` 455, 462, 594), and the padding that was here is the gap on both
      sides that was reported. */}
      <div className="min-h-0 flex-1">
        <DataList
          columnsKey="people"
          card={{
            title: 'name',
            context: 'email',
            defaults: { badge: 'status', details: ['roles', 'plan', 'projects', 'tracks'] },
          }}
          actions={
            props.onInvite === undefined
              ? []
              : [
                  {
                    label: t('invite.action'),
                    icon: 'plus' as const,
                    primary: true,
                    onSelect: props.onInvite,
                  },
                ]
          }
          columns={columns}
          rows={rows}
          isLoading={props.loading === true}
          sort={props.sort}
          onSortChange={props.onSortChange}
          /* ⚠️ Absent, not disabled, without the permission — `decisions.md` R-6. */
          bulkActions={[
            ...(props.onBulkEdit === undefined
              ? []
              : [
                  /**
                   * R-60 · ⚠️ **ADD AND REMOVE, NEVER *SET*.** A person can be in several teams,
                   * so putting six people in *Delivery* must not take them out of everything
                   * else they are in. Absent while the workspace has no teams.
                   */
                  ...(teams.length === 0
                    ? []
                    : [
                        {
                          label: t('people.team.add'),
                          icon: 'users' as const,
                          menu: (ids: string[]) =>
                            teams.map((team) => ({
                              label: team.name,
                              onSelect: () => {
                                props.onBulkEdit?.(ids, { addTeamId: team.id });
                              },
                            })),
                        },
                        {
                          label: t('people.team.remove'),
                          icon: 'users' as const,
                          menu: (ids: string[]) =>
                            teams.map((team) => ({
                              label: team.name,
                              onSelect: () => {
                                props.onBulkEdit?.(ids, { removeTeamId: team.id });
                              },
                            })),
                        },
                      ]),
                  {
                    label: t('people.column.employment'),
                    icon: 'building' as const,
                    /* ⚠️ One `t()` per branch with a LITERAL key — `i18n:check` reads literal
                       arguments only, and a computed key reports every entry as an orphan. */
                    menu: (ids: string[]) => [
                      {
                        label: t('people.employment.internal'),
                        onSelect: () => {
                          props.onBulkEdit?.(ids, { employmentType: 'internal' });
                        },
                      },
                      {
                        label: t('people.employment.contractor'),
                        onSelect: () => {
                          props.onBulkEdit?.(ids, { employmentType: 'contractor' });
                        },
                      },
                    ],
                  },
                ]),
            ...(props.onBulkDeactivate === undefined
              ? []
              : [
                  {
                    label: t('people.action.deactivate'),
                    icon: 'trash' as const,
                    destructive: true,
                    onSelect: (ids: string[]) => {
                      props.onBulkDeactivate?.(ids);
                    },
                  },
                ]),
          ]}
          search={props.search}
          onSearchChange={props.onSearch}
          filters={[
            {
              key: 'status',
              label: t('people.column.status'),
              value: props.status,
              onChange: props.onStatus,
              options: [
                { value: '', label: t('common.all') },
                { value: 'active', label: t('people.status.active') },
                { value: 'invited', label: t('people.status.invited') },
                { value: 'deactivated', label: t('people.status.deactivated') },
              ],
            },
            /* R-60 · the shared team filter; `props.team` is a team id. */
            ...teamFilter(t, teams, props.team, props.onTeam),
            {
              key: 'employment',
              label: t('people.column.employment'),
              value: props.employmentType,
              onChange: props.onEmploymentType,
              options: [
                { value: '', label: t('common.all') },
                { value: 'internal', label: t('people.employment.internal') },
                { value: 'contractor', label: t('people.employment.contractor') },
              ],
            },
            {
              key: 'tracks',
              label: t('people.column.tracks'),
              value: props.tracks,
              onChange: props.onTracks,
              options: [
                { value: '', label: t('common.all') },
                { value: 'true', label: t('people.tracks.yes') },
                { value: 'false', label: t('people.tracks.no') },
              ],
            },
            ...(props.onPlan === undefined
              ? []
              : [
                  {
                    key: 'plan',
                    label: t('people.column.plan'),
                    value: props.plan ?? '',
                    onChange: props.onPlan,
                    options: [
                      { value: '', label: t('common.all') },
                      { value: 'true', label: t('people.plan.yes') },
                      { value: 'false', label: t('people.plan.no') },
                    ],
                  },
                ]),
            {
              /* ⚠️ **The tombstones are a FILTER, not a hidden state.** A worklog report names
               * them, so somebody will look for one — and they are off by default or every list
               * grows with people who left. */
              key: 'deleted',
              label: t('people.filter.deleted'),
              value: props.includeDeleted ? 'yes' : '',
              onChange: (value) => {
                props.onIncludeDeleted(value === 'yes');
              },
              options: [
                { value: '', label: t('people.filter.liveOnly') },
                { value: 'yes', label: t('people.filter.withDeleted') },
              ],
            },
          ]}
          onRowClick={(id) => {
            const person = people.find((candidate) => candidate.id === id);
            /* ⚠️ **An invitation opens nothing.** There is no person behind it yet, and
               `/people/<an invitation id>` is a 404 wearing a click. */
            if (person?.kind === 'member') props.onOpen(person);
          }}
          rowMenu={rowMenu}
          empty={{
            icon: 'users',
            title: t('people.empty'),
            body: t('people.emptyNote'),
          }}
          {...(props.page === undefined ? {} : { page: props.page })}
          labels={listLabels(t)}
        />
      </div>

      {/* ⚠️ C17 replaced a sentence here — *"pending invitations arrive with the invitation
          surface"* — with the surface. See `invitations-panel.tsx` on why they are their own block
          rather than ghost rows in the table above. */}
      {props.invitations}
    </div>
  );
}
